import {z} from "zod";
import {useState, useEffect} from "react";
import {useSave} from "@/contexts/SaveContext";

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Generic validation function that can be used by any setting component
 * @param value - The value to validate
 * @param validationSchema - Zod schema to validate against
 * @param setError - Function to set error state
 * @returns ValidationResult
 */
export const validateSetting = (
  value: any,
  validationSchema: z.ZodSchema | undefined,
  setError: (error: string) => void,
): ValidationResult => {
  if (!validationSchema) {
    return {isValid: true};
  }

  const result = validationSchema.safeParse(value);

  if (!result.success) {
    const errorMessage = result.error.errors[0]?.message || "Invalid value";
    setError(errorMessage);
    return {isValid: false, error: errorMessage};
  } else {
    setError("");
    return {isValid: true};
  }
};

/**
 * Hook for setting up validation in any setting component
 * Automatically handles blockSave state based on validation results
 * @param validationSchema - Zod schema from the setting parameter
 * @returns Object with validate function and error state
 */
export const useValidation = (validationSchema: z.ZodSchema | undefined) => {
  const [error, setError] = useState<string>("");
  const {setBlockSave} = useSave();

  // Block save whenever there's an error
  useEffect(() => {
    if (error) {
      setBlockSave(true);
    } else {
      setBlockSave(false);
    }
  }, [error, setBlockSave]);

  const validate = (value: any) => {
    const result = validateSetting(value, validationSchema, setError);
    return result;
  };

  return {
    error,
    validate,
  };
};
